import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2Icon, ArrowUpIcon, ArrowDownIcon, MinusIcon, ArrowRightIcon, Mountain, Calendar, Music, Rss, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { chartSources } from '../data/chartSources';
import { supabase } from '../utils/supabase';
import { AudioPreview } from '../components/AudioPreview';
import { formatTimeAgo } from '../utils/dateUtils';

interface ChartEntry {
  rank: number;
  name: string;
  artist: string;
  last_week_rank: number | null;
  isNew: boolean;
  peak_rank: number;
  weeks_on_chart: number;
  image: string;
  previewUrl: string;
}

interface ChartData {
  date: string;
  data: ChartEntry[];
}

function getRankChange(currentRank: number, lastWeek: number | null): { direction: 'up' | 'down' | 'same' | 'new', change: number } {
  if (lastWeek === null) {
    return { direction: 'new', change: 0 };
  }
  
  if (currentRank === lastWeek) {
    return { direction: 'same', change: 0 };
  }
  
  return currentRank < lastWeek 
    ? { direction: 'up', change: lastWeek - currentRank }
    : { direction: 'down', change: currentRank - lastWeek };
}

async function fetchChartData(chartId: string): Promise<ChartData> {
  try {
    console.log('Fetching chart data for:', chartId);
    const { data, error } = await supabase
      .from('charts')
      .select('*')
      .eq('id', chartId)
      .single();

    if (error) {
      console.error('Supabase error:', error);
      throw new Error(`Failed to fetch chart data: ${error.message}`);
    }

    if (!data) {
      throw new Error('No chart data found');
    }

    console.log('Fetched data:', data);
    return {
      date: data.date,
      data: data.data
    };
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

const ChartPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const source = chartSources.find(s => s.id === id);
  const Icon = source?.logo;

  const { data: chartData, isLoading, error } = useQuery({
    queryKey: ['chart', id],
    queryFn: () => fetchChartData(id || ''),
    enabled: !!id
  });

  if (!source) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Chart not found</h2>
          <Link to="/" className="text-indigo-600 hover:text-indigo-500 mt-4 inline-block">
            Return to homepage
          </Link>
        </div>
      </main>
    );
  }

  // Check if it's not a Billboard chart
  const isBillboardChart = source.id.startsWith('billboard');
  if (!isBillboardChart) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-indigo-600 hover:text-indigo-500 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Charts
        </Link>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-3xl font-bold text-white">Coming Soon</span>
          </div>
          <div className="flex items-center space-x-4 mb-6">
            {Icon && <Icon className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{source.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">{source.description}</p>
            </div>
          </div>
          
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            View original chart →
          </a>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <Loader2Icon className="w-8 h-8 animate-spin mx-auto" />
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading chart data...</p>
        </div>
      </main>
    );
  }

  if (error) {
    console.error('Error fetching chart data:', error);
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Error loading chart data</h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Please try again later</p>
        </div>
      </main>
    );
  }

  if (!chartData || !chartData.data) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">No chart data available</h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Please try again later</p>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link
        to="/"
        className="inline-flex items-center text-indigo-600 hover:text-indigo-500 mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Charts
      </Link>
      
      <div className="glass-card rounded-2xl shadow-lg p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-6">
            {Icon && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                <Icon className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">{source.name}</h1>
              <p className="text-gray-600 dark:text-gray-400 max-w-2xl">{source.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-button px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center space-x-2 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              <span>View original chart</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{source.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last updated: {formatTimeAgo(chartData.date)}</p>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href={`/.netlify/functions/csv/${id}`}
                className="glass-button px-3 sm:px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center space-x-2 hover:text-green-600 dark:hover:text-green-400"
                download
              >
                <span className="hidden sm:inline">Download CSV</span>
                <ArrowDownIcon className="w-4 h-4" />
              </a>
              <a
                href={`/rss/${source.id}.xml`}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-button px-3 sm:px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center space-x-2 hover:text-orange-600 dark:hover:text-orange-400"
              >
                <span className="hidden sm:inline">RSS Feed</span>
                <Rss className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
          {chartData.data.map((entry) => {
            const rankChange = getRankChange(entry.rank, entry.last_week_rank);
            return (
              <div
                key={entry.rank}
                className="group px-6 py-4 flex items-center space-x-4 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors duration-200"
              >
                <div className="flex-shrink-0 w-16 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{entry.rank}</div>
                  <div className="mt-1">
                    {rankChange.direction === 'up' && (
                      <div className="flex items-center justify-center text-green-500">
                        <span className="text-xs font-medium">+{rankChange.change}</span>
                        <ArrowUpIcon className="w-4 h-4 ml-0.5" />
                      </div>
                    )}
                    {rankChange.direction === 'down' && (
                      <div className="flex items-center justify-center text-red-500">
                        <span className="text-xs font-medium">-{rankChange.change}</span>
                        <ArrowDownIcon className="w-4 h-4 ml-0.5" />
                      </div>
                    )}
                    {rankChange.direction === 'same' && (
                      <div className="flex items-center justify-center">
                        <MinusIcon className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    {rankChange.direction === 'new' && (
                      <div className="flex items-center justify-center text-blue-500">
                        <span className="text-xs font-medium">NEW</span>
                      </div>
                    )}
                  </div>
                </div>

                {entry.image && (
                  <div className="flex-shrink-0">
                    <img
                      src={entry.image}
                      alt=""
                      className="w-16 h-16 object-cover rounded-lg shadow-sm group-hover:shadow transition-shadow duration-200"
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-medium text-gray-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-200">
                    {entry.name}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
                    {entry.artist}
                  </p>
                </div>

                <div className="flex-shrink-0 flex items-center space-x-4">
                  <div className="flex flex-col items-end space-y-2 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1" title="Peak Position">
                      <span>#{entry.peak_rank}</span>
                      <span className="hidden sm:inline mr-1">Peak</span>
                      <Mountain className="w-4 h-4" />
                    </div>
                    <div className="flex items-center gap-1" title="Weeks on Chart">
                      <span>{entry.weeks_on_chart}</span>
                      <span className="hidden sm:inline mr-1">Weeks</span>
                      <Calendar className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default ChartPage;